import React, { MouseEvent, useCallback, useMemo, useRef, useState } from 'react';

import cx from 'classnames';
import { sort } from 'ramda';

import { parseStringToDate } from 'components/Pages/Admin/Itinerary/Details/DetailsChronologicalMode/EventCalendar/Calendar/utils/days-generator/helpers';
import { TDayTripEvent } from 'components/Pages/Admin/Itinerary/Details/DetailsChronologicalMode/types';
import { sortTripEventsByType } from 'components/Pages/Admin/Itinerary/Details/DetailsChronologicalMode/utils';
import { FontAwesomeIcon } from 'components/shared/Icon/FontAwesome';
import { AirplaneTilt, Bed, MapTrifold } from 'components/shared/Icon/phosphorIcons';
import { Label } from 'components/shared/Label';
import Text from 'components/shared/Typography/Text';
import { useLandingV2Context } from 'detailsv2/LandingV2/context';
import { EOrganizedBy } from 'detailsv2/structure';
import useMediaBreakpoints from 'hooks/useMediaBreakpoints';
import i18n from 'i18n';
import { getDateFormat } from 'utils/date';
import { getTranslationDateFormat } from 'utils/i18n/date';
import { formatDatesRangeWithoutParseISO } from 'utils/itineraryDetails';
import { Maybe } from 'utils/types';

import { useShowNavigationModal } from '..';
import { ActivitiesCell } from './ActivitiesCell';
import s from './index.module.css';
import {
  CellProps,
  EEventType,
  IEventType,
  IRooms,
  ITravelBriefAccommodation,
  TDate,
  TDateRange,
  TNightsCount,
  WithValue,
} from './types';

export const useTravelBriefTable = () => {
  const { data, readonly } = useLandingV2Context();
  const { travelBrief } = data;
  const { overall, flights: flightsSchedule, accommodations: accommodationSchedule } = travelBrief;
  const { isMobile, isTabletOrMobile } = useMediaBreakpoints();
  const wrapperRef = useRef<HTMLDivElement>(null);
  const { settings } = data;
  const showDatesAsDDMMYYYY = settings?.displayDatesDdMmYyyy;
  const timeFormat = settings?.displayTimes24h ? '24h' : '12h';
  const fullSchedule = overall.map((day) => {
    return {
      ...day,
      activities: sort(sortTripEventsByType, day.activities),
    };
  });

  const defaultEventsType: IEventType[] = useMemo(
    () => [
      {
        name: i18n.t('proposal_content.travel_brief.tabs.overview.title'),
        label: EEventType.All,
        icon: <MapTrifold />,
      },
      {
        name: i18n.t('proposal_content.travel_brief.tabs.flight.title'),
        label: EEventType.Flight,
        icon: <AirplaneTilt />,
      },
      {
        name: i18n.t('proposal_content.travel_brief.tabs.accommodation.title'),
        label: EEventType.Accommodation,
        icon: <Bed />,
      },
    ],
    [readonly, i18n.version],
  );

  const eventsType = useMemo(
    () =>
      defaultEventsType.filter((eventType) => {
        if (!flightsSchedule.length && eventType.label === EEventType.Flight) {
          return false;
        } else if (!accommodationSchedule.length && eventType.label === EEventType.Accommodation) {
          return false;
        }

        return true;
      }),
    [defaultEventsType, i18n.version],
  );

  const [activeType, setActiveType] = useState(defaultEventsType[0].label);

  const onChangeType = (label: string, e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
    setActiveType(label);
    if (!isTabletOrMobile) {
      wrapperRef.current?.scrollIntoView(true);
    }
  };

  const getDate = useCallback(
    (date: string) => {
      const format = `${isTabletOrMobile ? '%a' : '%A'} ${getTranslationDateFormat(
        getDateFormat(showDatesAsDDMMYYYY),
      )}`;
      const localizedDate = i18n.strftime(parseStringToDate(date.slice(0, 10)), format);

      return (
        <div className={s.date}>
          {localizedDate.split(' ').map((part, index) => (
            <span key={index}>
              {part + ' '}
              {index === 0 && <br />}
            </span>
          ))}
        </div>
      );
    },
    [isTabletOrMobile, showDatesAsDDMMYYYY],
  );

  const getRegularColumns = (secondColumnName: string) => {
    const displayDate = secondColumnName == 'Flight' ? displayTravelBriefDate : displayTravelBriefGroupedDate;
    const displayTitle =
      secondColumnName == 'Flight'
        ? i18n.t('proposal_content.travel_brief.tabs.flight.tabs.flight')
        : i18n.t('proposal_content.travel_brief.tabs.overview.tabs.activities');

    return [
      {
        Header: <span data-block>{i18n.t('proposal_content.travel_brief.tabs.overview.tabs.day')}</span>,
        accessor: 'dates',
        fixedSize: '25%',
        className: cx(s.thDay, s.thNomadDate, 'custom-manifest-thDate'),
        Cell: displayDate(data.scheduleType, getDate),
      },
      {
        Header: <span data-block>{displayTitle}</span>,
        accessor: 'activities',
        fixedSize: '75%',
        className: cx(s.thAct, s.thNomadAcc, 'custom-manifest-thAcc'),
        Cell: ({ value }: WithValue<TDayTripEvent[]>) => {
          if (value.length === 0) return <NoEvents />;

          return value.map((tripEvent, i) => {
            return (
              <ActivitiesCell
                key={tripEvent.id + '_' + i}
                tripEvent={tripEvent}
                data={value}
                activeType={activeType}
                readonly={readonly}
                timeFormat={timeFormat}
              />
            );
          });
        },
      },
    ];
  };

  const regularColumns = useMemo(() => getRegularColumns('Activities'), [i18n.version, getDate, timeFormat]);

  const flightColumns = useMemo(() => getRegularColumns('Flight'), [i18n.version, getDate, timeFormat]);

  const showNavigationModal = useShowNavigationModal();

  const getEventTripId = (row: CellProps<any>['row']) => {
    return row?.original?.accommodation?.id;
  };

  const handleTripEventClick = (event: MouseEvent, eventTripId?: number) => {
    showNavigationModal(eventTripId);
    event.stopPropagation();
  };

  const accommodationColumns = useMemo(
    () =>
      [
        {
          Header: <span data-block>{i18n.t('proposal_content.travel_brief.tabs.accommodation.tabs.day')}</span>,
          accessor: 'dates',
          fixedSize: '17%',
          className: cx(s.thDay, s.thNomadDate, 'custom-manifest-thDate'),
          Cell: (props: CellProps<TDateRange>) => {
            const { value, row } = props;
            const eventTripId = getEventTripId(row);

            return (
              <div data-block onClick={(e) => handleTripEventClick(e, eventTripId)}>
                {data.scheduleType === EOrganizedBy.Day ? (
                  <>
                    {i18n.t('proposal_content.travel_brief.tabs.accommodation.tabs.day')} {value.daysRange.start}{' '}
                    {value.daysRange.end && value.daysRange.end - value.daysRange.start
                      ? `- ${value.daysRange.end}`
                      : null}
                  </>
                ) : (
                  <>
                    {formatDatesRangeWithoutParseISO(
                      { start: value.datesRange.start, end: value.datesRange.end },
                      {
                        includeOnlyDifferentYears: false,
                        translate: true,
                        translationDateFormat: getDateFormat(showDatesAsDDMMYYYY),
                      },
                    )}
                  </>
                )}
              </div>
            );
          },
        },
        {
          Header: (
            <span data-block>{i18n.t('proposal_content.travel_brief.tabs.accommodation.tabs.accommodation')}</span>
          ),
          accessor: 'accommodation',
          fixedSize: '33%',
          className: cx(s.thAcc, s.thNomadAcc, 'custom-manifest-thAcc'),
          Cell: ({ value, row }: CellProps<ITravelBriefAccommodation>) => {
            const { name, isAlternate, nights } = value;
            const eventTripId = getEventTripId(row);

            return (
              <div data-block className={s.eventContainer} onClick={(e) => handleTripEventClick(e, eventTripId)}>
                {name}
                {isAlternate ? (
                  <div className={s.labelContainer}>
                    <Label
                      color="#65ADFF"
                      text={i18n.t('proposal_content.event_details.tags.alternate')}
                      className={s.alternateLabel}
                    />
                  </div>
                ) : (
                  ''
                )}
                {isMobile && Boolean(nights) && (
                  <>
                    <br />
                    <Text italic isStrong data-block>
                      {i18n.t('proposal_content.nights', { count: nights })}
                    </Text>
                  </>
                )}
              </div>
            );
          },
        },
        {
          Header: <span data-block>{i18n.t('proposal_content.travel_brief.tabs.accommodation.tabs.details')}</span>,
          accessor: 'rooms',
          fixedSize: 'auto',
          className: cx(s.thRoom, s.thNomadAcc, 'custom-manifest-thAcc'),
          Cell: ({ value, row }: CellProps<IRooms>) => {
            const { rooming, roomBasis } = value;
            const eventTripId = getEventTripId(row);

            return (
              <div data-block className={s.eventContainer} onClick={(e) => handleTripEventClick(e, eventTripId)}>
                {rooming?.map(({ name, count }, i) => {
                  return (
                    <div key={name + i} style={{ display: 'flex', flexDirection: 'column' }}>
                      <span>
                        {name || ''} {count ? `x ${count}` : ''}
                      </span>
                    </div>
                  );
                })}
                {roomBasis ? (
                  <Text italic size="medium" data-block>
                    {roomBasis}
                  </Text>
                ) : (
                  !rooming?.length && '-'
                )}
              </div>
            );
          },
        },
        !isMobile && {
          Header: <span data-block>{i18n.t('proposal_content.travel_brief.tabs.accommodation.tabs.nights')}</span>,
          accessor: 'nights',
          fixedSize: '70px',
          className: cx(s.thNights, s.thNomadAcc, 'custom-manifest-thAcc'),
          Cell: ({ value, row }: CellProps<TNightsCount>) => {
            const eventTripId = getEventTripId(row);

            return <div onClick={(e) => handleTripEventClick(e, eventTripId)}>{value || '-'}</div>;
          },
        },
      ].filter(Boolean),
    [data.scheduleType, i18n.version, showDatesAsDDMMYYYY],
  );

  return {
    eventsType,
    activeType,
    onChangeType,
    accommodationColumns,
    regularColumns,
    flightColumns,
    fullSchedule,
    flightsSchedule,
    accommodationSchedule,
    wrapperRef,
  };
};

export const NoEvents = () => {
  return (
    <div className={s.noEvents}>
      <FontAwesomeIcon className="fa-sign-post fa-light" />
      <span data-block>{i18n.t('proposal_content.event_details.no_events')}</span>
    </div>
  );
};

/**
 * Function for combining the logic of displaying grouped dates for Landing and PDF Travel Brief
 * @param scheduleType how is the proposal organized by 'dates' | 'days'
 * @param fn the function for rendering the date
 */
export const displayTravelBriefGroupedDate = (scheduleType: EOrganizedBy, fn: (date: string) => JSX.Element) => {
  return ({ value }: WithValue<TDateRange>) => {
    if (!value?.daysRange?.start) return null;

    const startDateText = getDateText(value.daysRange.start, value.datesRange.start);

    const endDateText = (() => {
      if (!value.daysRange.end) return null;
      if (value.daysRange.start === value.daysRange.end) return null;

      return (
        <>
          {/* new line only when organized by date */}
          {` - ${scheduleType === EOrganizedBy.Date ? '\n' : ''}`}
          {getDateText(value.daysRange.end, value.datesRange.end)}
        </>
      );
    })();

    return (
      <div data-block>
        {startDateText}
        {endDateText}
      </div>
    );
  };

  function getDateText(day: number, date: Maybe<string>) {
    return (
      <>
        {i18n.t('proposal_content.day', { count: day })}{' '}
        {date && scheduleType === EOrganizedBy.Date ? fn(date) : null}
      </>
    );
  }
};

/**
 * Function for combining the logic of displaying basic dates for Landing and PDF Travel Brief
 * @param scheduleType how is the proposal organized by 'dates' | 'days'
 * @param fn the function for rendering the date
 */
export const displayTravelBriefDate = (scheduleType: EOrganizedBy, fn: (date: string) => JSX.Element) => {
  return ({ value }: WithValue<TDate>) => {
    if (!value.day) {
      return null;
    }

    return (
      <div data-block>
        {i18n.t('proposal_content.day', { count: value.day })}{' '}
        {value.date && scheduleType === EOrganizedBy.Date ? fn(value.date) : null}
      </div>
    );
  };
};
